import React, { useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Shared } from 'ui/shared'
import { Header } from './components/Header/Header'
import RentStatusStepper from './components/RentStatusStepper/RentStatusStepper'
import { Card, CardHeader, CardContent, Grid, Typography, Button, Box, Link } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Actions_Bookings } from 'redux/modules/bookings/Actions'
import { useSelector } from 'react-redux'
import { TState } from '../../../redux/config/root-types'
import DateConverter from 'utils/helpers/date'
import { Actions_CurrentTrailer } from 'redux/modules/currentTrailer/Actions'
import { To } from 'router/To'
import { CancelBookingModal } from './components/CancelBookingModal/CancelBookingModal'

export const BookingDetails = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { id } = useParams()

  const booking = useSelector((state: TState) => state.bookings.currentBooking)

  const [isOpenCancelBooking, setisOpenCancelBooking] = useState(false)

  useEffect(() => {
    dispatch(Actions_Bookings.saga.getBookingDetails({ id }))
  }, [id])

  if (!booking) {
    return null
  }

  const onShowOnMapClick = () => {
    if (!booking) return
    const trailerData = {
      id: booking.trailer.id,
      lat: booking.trailer.latitude,
      lng: booking.trailer.longitude,
    }
    const navigateToTrailersMap = () => navigate(To.trailersMap)
    dispatch(Actions_CurrentTrailer.saga.showOnMap({ ...trailerData, navigateToTrailersMap }))
  }

  const openWatsapp = () => {
    const isPhoneExist = !!booking.user?.phone
    const userPhone = isPhoneExist ? booking.user.phone.replaceAll('+', '') : ''
    const whatsAppLink = isPhoneExist ? `https://wa.me/${userPhone}` : ''

    window.open(whatsAppLink, '_blank')
  }

  return (
    <Shared.Page className={classes.root}>
      <Header onPressCancel={() => setisOpenCancelBooking(true)} />

      <RentStatusStepper
        className={classes.rentStatusStepper}
        status={booking.state}
        canceled={booking.booking.isFutureBookingCancelled}
      />

      <Grid container spacing={'1rem'} className={classes.cardsContainer}>
        {/* Detail information */}
        <Grid item xs={12} md={6} lg={3}>
          <Card className={classes.card} elevation={0}>
            <CardHeader
              title="Detail information"
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
              className={classes.cardHeader}
            />
            <CardContent>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Type
                </Typography>
                <Typography variant="body1">
                  {booking.booking.source === 'crm' ? 'Special rent' : booking?.booking.rentType}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Location start
                </Typography>
                <Typography variant="body1">{`${booking.parking?.city}, ${booking.parking?.street}`}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Location finish
                </Typography>
                <Typography variant="body1">{`${booking.finishParking?.city}, ${booking.finishParking?.street}`}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Estimated time
                </Typography>
                <Typography variant="body1">
                  {DateConverter.formatDate(booking.booking.rentStart) || ''} -{' '}
                  {DateConverter.formatDate(booking.booking.rentEnd) || ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Rent time
                </Typography>
                <Typography variant="body1">
                  {DateConverter.formatDate(booking.booking.startDate) || ''} -{' '}
                  {DateConverter.formatDate(booking.booking.endDate) || ''}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Trailer */}
        <Grid item xs={12} md={6} lg={3}>
          <Card className={classes.card} elevation={0}>
            <CardHeader
              title="Trailer"
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
              className={classes.cardHeader}
            />
            <CardContent>
              <Box mb={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" color="textSecondary">
                  Government number
                </Typography>
                <Link href="#" color="orange" underline="hover">
                  {booking.trailer.plate}
                </Link>
              </Box>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Type
                </Typography>
                <Typography variant="body1">{booking.trailer.trailerType.name}</Typography>
              </Box>
              <Button variant="outlined" fullWidth className={classes.orangeButton} onClick={onShowOnMapClick}>
                On map
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* User contacts */}
        <Grid item xs={12} md={6} lg={3}>
          <Card className={classes.card} elevation={0}>
            <CardHeader
              title="User contacts"
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
              className={classes.cardHeader}
            />
            <CardContent>
              <Box mb={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" color="textSecondary">
                  User name
                </Typography>
                <Link href="#" color="orange" underline="hover">
                  {booking.user.firstName} {booking.user.lastName}
                </Link>
              </Box>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Phone
                </Typography>
                <Typography variant="body1">{booking.user.phone}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">{booking.user.email}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Address
                </Typography>
                <Typography variant="body1">{booking.user.address || '-'}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Postal code
                </Typography>
                <Typography variant="body1">{booking.user.postCode || '-'}</Typography>
              </Box>
              <Button variant="outlined" fullWidth className={classes.orangeButton} onClick={openWatsapp}>
                Chat in WhatsApp
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Invoice */}
        <Grid item xs={12} md={6} lg={3}>
          <Card className={classes.card} elevation={0}>
            <CardHeader
              title="Invoice"
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
              className={classes.cardHeader}
            />
            <CardContent>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Invoice
                </Typography>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                  <Link href="#" color="orange" underline="hover">
                    {booking.invoiceSerial || '-'}
                  </Link>
                  <Typography variant="body1" ml={1}>
                    {DateConverter.formatDate(booking.invoiceDate) || ''}
                  </Typography>
                </Box>
              </Box>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Additional invoice
                </Typography>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                  <Link href="#" color="orange" underline="hover">
                    {booking.additionalInvoiceSerial || '-'}
                  </Link>
                  <Typography variant="body1" ml={1}>
                    {DateConverter.formatDate(booking.additionalInvoiceDate) || ''}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Refund invoice
                </Typography>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                  <Link href="#" color="orange" underline="hover">
                    {booking.refundInvoiceSerial || '-'}
                  </Link>
                  <Typography variant="body1" ml={1}>
                    {DateConverter.formatDate(booking.refundInvoiceDate) || ''}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <CancelBookingModal isOpen={isOpenCancelBooking} closeModal={() => setisOpenCancelBooking(false)} />
    </Shared.Page>
  )
}

const useStyles = makeStyles()((theme) => ({
  root: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    padding: '1.5rem',
    paddingTop: 0,
    backgroundColor: theme.palette.background.default,
    height: '100%',
  },
  rentStatusStepper: {
    marginTop: '1rem',
    borderRadius: 4,
    border: '1px solid #E5E7EB',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  cardsContainer: {
    marginTop: '1px',
    flexGrow: 1,
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 4,
    border: '1px solid #E5E7EB',
    '& .MuiCardContent-root': {
      flexGrow: 1,
    },
  },
  cardHeader: {
    backgroundColor: theme.palette.background.paper,
    //borderBottom: `1px solid ${theme.palette.divider}`,
  },
  orangeButton: {
    borderColor: 'orange',
    color: 'orange',
    textTransform: 'initial',
    '&:hover': {
      backgroundColor: 'orange',
      color: 'white',
    },
  },
}))
